import { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchSentimentProducts,
  fetchIssueDistribution,
  fetchSentimentEvents,
  fetchSocialCrawlStatus,
  fetchCrawlProgress,
  abortSocialCrawl,
  triggerXhsProductCrawl,
  ProductSentiment,
  SocialCrawlStatus,
  CrawlProgress,
  IssueDistribution,
} from "../../api";

import {
  COMPANY_DOT_COLOR,
  riskColor,
  defaultRange,
  toDateStr,
  groupByCompany,
} from "./shared";

import IssueDistChart from "./IssueDistChart";
import EventTimeline from "./EventTimeline";
import PostsPanel from "./PostsPanel";
import AISummary from "./AISummary";
import WordCloudPanel from "./WordCloudCard";

// ─────────────────────── Company Dot ───────────────────────────────────

function CompanyDot({ company }: { company: string }) {
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[9px] font-bold ${COMPANY_DOT_COLOR[company] ?? "bg-ink-faint"}`}>
      {company.charAt(0)}
    </span>
  );
}

// ─────────────────────── Product Row (sidebar item) ────────────────────

function ProductRow({ data, active, onClick }: { data: ProductSentiment; active: boolean; onClick: () => void }) {
  const rc = riskColor(data.risk_index);
  const total = (data.positive + data.negative + data.neutral) || 1;
  const negPct = Math.round((data.negative / total) * 100);

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ${
        active
          ? "border-primary-200 bg-primary-50/70 shadow-soft dark:border-primary-800 dark:bg-primary-900/20"
          : "border-transparent hover:border-border hover:bg-surface"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex-1 truncate text-sm font-medium text-ink">{data.product}</span>
        <span className="text-2xs tabular-nums text-ink-tertiary">{data.post_count}条</span>
        {negPct > 0 && (
          <span className={`text-2xs tabular-nums font-medium ${negPct >= 40 ? "text-rose-600 dark:text-rose-400" : "text-ink-tertiary"}`}>
            负{negPct}%
          </span>
        )}
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${rc.dot}`} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-ink-faint">
        <span>{rc.label}</span>
        <span className="font-mono tabular-nums">Risk {data.risk_index}</span>
      </div>
    </button>
  );
}

// ─────────────────────── Stat Badge ────────────────────────────────────

function StatBadge({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className={`flex min-w-[72px] flex-col items-center rounded-xl px-3 py-2 ${cls}`}>
      <span className="data-num text-[15px] font-semibold leading-none">{value}</span>
      <span className="mt-1 text-[10px] tracking-wide opacity-75">{label}</span>
    </div>
  );
}

// ─────────────────────── Main Page ─────────────────────────────────────

type TabId = "issues" | "events" | "posts" | "cloud";
const TABS: { id: TabId; label: string }[] = [
  { id: "issues", label: "问题分布" },
  { id: "events", label: "舆情事件" },
  { id: "posts",  label: "原始帖子" },
  { id: "cloud",  label: "词云"     },
];

const SENTIMENT_QUERY_KEYS = [
  "sentimentProducts", "issueDistribution", "sentimentEvents",
  "sentimentSummary", "sentimentPosts", "sentimentWordCloud",
];

export default function SentimentPage() {
  const [activeProduct, setActiveProduct] = useState<string | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabId>("issues");
  const [dateFrom,      setDateFrom]      = useState(defaultRange().from);
  const [dateTo,        setDateTo]        = useState(defaultRange().to);
  const [crawling,      setCrawling]      = useState(false);
  const [triggerTime,   setTriggerTime]   = useState<string | null>(null);
  const [crawlProduct,  setCrawlProduct]  = useState<string | null>(null);
  const [showSidebar,   setShowSidebar]   = useState(false);
  const queryClient = useQueryClient();

  const invalidateAll = useCallback(() => {
    SENTIMENT_QUERY_KEYS.forEach(k => queryClient.invalidateQueries({ queryKey: [k] }));
  }, [queryClient]);

  // Poll crawl status
  const { data: crawlStatus } = useQuery({
    queryKey: ["socialCrawlStatus"],
    queryFn: fetchSocialCrawlStatus,
    refetchInterval: (query) => {
      const st = (query.state.data as SocialCrawlStatus | undefined)?.status;
      return (crawling || st === "running") ? 4_000 : false;
    },
    staleTime: 0,
  });

  // Poll crawl progress (when crawling)
  const { data: crawlProgress } = useQuery<CrawlProgress>({
    queryKey: ["crawlProgress"],
    queryFn: fetchCrawlProgress,
    refetchInterval: crawling ? 2_000 : false,
    enabled: crawling,
    staleTime: 0,
  });

  useEffect(() => {
    if (!crawlStatus) return;
    const { status } = crawlStatus;
    if (crawling && (status === "success" || status === "failed")) {
      const startedAt = crawlStatus.started_at ? new Date(crawlStatus.started_at) : null;
      const triggered = triggerTime ? new Date(triggerTime) : null;
      if (!triggered || !startedAt || startedAt >= triggered) {
        setCrawling(false);
        invalidateAll();
      }
    }
    if (!crawling && status === "running") setCrawling(true);
  }, [crawlStatus]);

  useEffect(() => {
    if (crawlProgress?.phase === "done" || crawlProgress?.phase === "aborted" || crawlProgress?.phase === "error") {
      setCrawling(false);
      setCrawlProduct(null);
      invalidateAll();
    }
  }, [crawlProgress?.phase]);

  const handleTriggerCrawl = useCallback(async (product?: string) => {
    const target = product || activeProduct;
    if (!target) return;
    const now = new Date().toISOString();
    setTriggerTime(now);
    setCrawling(true);
    setCrawlProduct(target);
    try {
      const result = await triggerXhsProductCrawl(target);
      if (result.status === "need_login") {
        setCrawling(false);
        setCrawlProduct(null);
        alert("⚠️ 小红书登录已过期\n\n请在调试 Chrome 浏览器中重新登录小红书，然后再点击刷新。");
      } else if (result.status === "no_chrome") {
        setCrawling(false);
        setCrawlProduct(null);
        alert("⚠️ Chrome 调试端口未连接\n\n请先启动调试模式 Chrome 并登录小红书。");
      } else if (result.status === "already_running") {
        // Already crawling, just show progress
      }
    } catch {
      setCrawling(false);
      setCrawlProduct(null);
    }
  }, [activeProduct]);

  const handleAbortCrawl = useCallback(async () => {
    try { await abortSocialCrawl(); } catch { /* ignore */ }
  }, []);

  const { data: products = [], isLoading: productsLoading } = useQuery<ProductSentiment[]>({
    queryKey: ["sentimentProducts", dateFrom, dateTo],
    queryFn: () => fetchSentimentProducts(dateFrom, dateTo),
    staleTime: 5 * 60 * 1000,
  });

  const sortedProducts = [...products].sort((a, b) => b.risk_index - a.risk_index);
  const companyGroups = groupByCompany(sortedProducts);
  const activeData = products.find((p) => p.product === activeProduct);
  const totalPosts = sortedProducts.reduce((sum, item) => sum + item.post_count, 0);
  const averageRisk = sortedProducts.length ? Math.round(sortedProducts.reduce((sum, item) => sum + item.risk_index, 0) / sortedProducts.length) : 0;
  const highestRiskProduct = sortedProducts[0];
  const dateRangeLabel = `${dateFrom} — ${dateTo}`;

  const { data: issues = [], isLoading: issuesLoading } = useQuery<IssueDistribution[]>({
    queryKey: ["issueDistribution", activeProduct, dateFrom, dateTo],
    queryFn: () => fetchIssueDistribution(activeProduct!, dateFrom, dateTo),
    enabled: !!activeProduct && activeTab === "issues",
    staleTime: 5 * 60 * 1000,
  });

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ["sentimentEvents", activeProduct, dateFrom, dateTo],
    queryFn: () => fetchSentimentEvents(activeProduct!, dateFrom, dateTo),
    enabled: !!activeProduct && activeTab === "events",
    staleTime: 5 * 60 * 1000,
  });

  function handleSelectProduct(product: string) {
    setActiveProduct(prev => prev === product ? null : product);
    setActiveTab("issues");
    setShowSidebar(false);
  }
  function setPreset(days: number) {
    const to = new Date(), from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setDateFrom(toDateStr(from)); setDateTo(toDateStr(to));
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Sentiment Desk</p>
          <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">产品舆情监测台</h1>
              <p className="text-sm leading-6 text-ink-secondary">
                聚合各产品社交舆情，按负面集中度、事件脉络与关键词分布呈现，辅助判断当前舆情风险水位。
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-2xs text-ink-tertiary">
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">产品 {sortedProducts.length}</span>
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">帖子 {totalPosts}</span>
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">平均风险 {averageRisk}</span>
              {highestRiskProduct && (
                <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-medium text-ink-secondary">
                  最高风险 · {highestRiskProduct.product}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 text-2xs text-ink-tertiary sm:px-6">
          <span className="text-label">监测窗口</span>
          <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">{dateRangeLabel}</span>
          {crawling && crawlProgress && crawlProgress.phase !== "idle" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-full border border-amber-200/70 bg-amber-50 px-2.5 py-1 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                  <path d="M14 8a6 6 0 00-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span className="font-medium">
                  {crawlProgress.phase === "aborting" ? "正在停止…" : (
                    <>
                      {crawlProduct && <>{crawlProduct} · </>}
                      {crawlProgress.current_keyword && <>{crawlProgress.current_keyword} · </>}
                      {crawlProgress.progress_pct !== undefined && <>{crawlProgress.progress_pct}%</>}
                      {crawlProgress.found !== undefined && <> · {crawlProgress.found}条</>}
                      {crawlProgress.eta_seconds != null && crawlProgress.eta_seconds > 0 && (
                        <> · 约{crawlProgress.eta_seconds > 60 ? `${Math.ceil(crawlProgress.eta_seconds / 60)}分钟` : `${crawlProgress.eta_seconds}秒`}</>
                      )}
                    </>
                  )}
                </span>
              </div>
              {crawlProgress.phase !== "aborting" && (
                <button
                  onClick={handleAbortCrawl}
                  className="rounded-full border border-rose-200/70 bg-rose-50 px-2 py-0.5 text-rose-600 transition-colors hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300 dark:hover:bg-rose-900/40"
                >
                  中止
                </button>
              )}
            </div>
          )}
          {!crawling && crawlStatus?.status === "running" && (
            <span className="rounded-full border border-amber-200/70 bg-amber-50 px-2.5 py-1 font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              正在抓取最新舆情
            </span>
          )}
          {crawlStatus?.status === "success" && crawlStatus.finished_at && !crawling && (
            <span className="rounded-full border border-border bg-surface px-2.5 py-1">
              最近刷新 {new Date(crawlStatus.finished_at).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      </section>

      <div className="flex min-h-0 flex-col gap-4 lg:flex-row">

        {/* ── Mobile: product selector bar ── */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setShowSidebar((v) => !v)}
            className="flex flex-1 items-center gap-2 rounded-xl border-subtle bg-surface-raised px-3 py-2 text-sm text-ink transition-colors hover:border-border-strong"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="8" cy="8" r="6.5" />
              <line x1="8" y1="5" x2="8" y2="11" />
              <line x1="5" y1="8" x2="11" y2="8" />
            </svg>
            {activeProduct || "选择产品"}
          </button>
          {activeProduct && (
            <button
              onClick={() => setActiveProduct(null)}
              className="rounded-xl border-subtle bg-surface-raised px-3 py-2 text-xs text-ink-tertiary transition-colors hover:border-border-strong hover:text-ink-secondary"
            >
              清除
            </button>
          )}
        </div>

        {/* ── Left Sidebar: product list ── */}
        <div className={clsx(
          "w-full shrink-0 flex-col gap-3 lg:flex lg:w-64",
          showSidebar ? "flex" : "hidden lg:flex"
        )}>

          {/* Date range toolbar */}
          <div className="paper-panel px-3 py-3 space-y-2.5">
            <div>
              <p className="text-label mb-2">时间窗口</p>
              <div className="flex items-center gap-1">
                {[{ label: "7天", days: 7 }, { label: "30天", days: 30 }, { label: "90天", days: 90 }].map(({ label, days }) => (
                  <button
                    key={days}
                    onClick={() => setPreset(days)}
                    className="flex-1 rounded-lg px-2 py-1.5 text-2xs font-medium text-ink-tertiary transition-colors hover:bg-surface hover:text-ink"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-lg border-subtle bg-surface px-2.5 py-1.5 text-2xs text-ink outline-none transition-colors focus:border-border-strong"
              />
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={toDateStr(new Date())}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-lg border-subtle bg-surface px-2.5 py-1.5 text-2xs text-ink outline-none transition-colors focus:border-border-strong"
              />
            </div>
            <div className="space-y-1.5">
              <button
                onClick={() => handleTriggerCrawl(activeProduct || undefined)}
                disabled={crawling || !activeProduct}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-700 dark:bg-accent/90 py-2 text-2xs font-semibold text-white dark:text-primary-900 transition-all hover:opacity-90 disabled:opacity-40 active:scale-[0.98]"
              >
                {crawling
                  ? <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />爬取{crawlProduct || ""}中...</>
                  : <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 8A6 6 0 1 1 8 2" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v4h-4" />
                      </svg>
                      {activeProduct ? `刷新「${activeProduct}」舆情` : "← 先选择产品"}
                    </>
                }
              </button>
              {!crawling && !activeProduct && (
                <button
                  onClick={() => {
                    if (confirm("全量爬取需要 ~30 分钟，且需要 Chrome 调试模式已登录小红书。\n\n确定要全量刷新所有产品舆情吗？\n\n建议在终端执行：\nscripts/xhs_cdp_crawl.py --all")) {
                      handleTriggerCrawl("花呗"); // Start with first product as demo
                    }
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border-subtle py-1.5 text-2xs text-ink-tertiary transition-all hover:border-border-strong hover:text-ink-secondary active:scale-[0.98]"
                >
                  全量刷新（~30分钟）
                </button>
              )}
            </div>
          </div>

          {/* Product list */}
          <div className="paper-panel flex-1">
            {productsLoading ? (
              <div className="space-y-1.5 p-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded-xl bg-surface" />
                ))}
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-ink-faint">
                <span className="text-[12px]">暂无舆情数据</span>
                <span className="text-[11px]">请在左侧选择产品后点击「刷新」获取最新数据</span>
              </div>
            ) : (
              <div className="p-2">
                {companyGroups.map(({ company, items }) => (
                  <div key={company} className="mb-3 last:mb-0">
                    <div className="mb-1 flex items-center gap-1.5 px-1">
                      <CompanyDot company={company} />
                      <span className="text-[10px] font-semibold tracking-[0.16em] text-ink-tertiary">{company}</span>
                    </div>
                    <div className="space-y-0.5">
                      {items.map(p => (
                        <ProductRow key={p.product} data={p}
                          active={activeProduct === p.product}
                          onClick={() => handleSelectProduct(p.product)} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Crawl status */}
          {!crawling && crawlStatus?.status === "success" && crawlStatus.articles_new !== undefined && (
            <div className="rounded-xl border border-emerald-200/70 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
              上次爬取 +{crawlStatus.articles_new} 条
              {crawlStatus.finished_at && (
                <span className="mt-0.5 block text-emerald-500 dark:text-emerald-400">
                  {new Date(crawlStatus.finished_at).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Mobile sidebar overlay backdrop ── */}
        {showSidebar && (
          <div
            className="fixed inset-0 z-30 bg-black/20 lg:hidden"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* ── Right: detail panel ── */}
        <div className="min-w-0 flex-1">
          {!activeData ? (
            <div className="flex min-h-[320px] h-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border-strong bg-surface-raised text-ink-faint sm:min-h-[420px]">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm text-ink-secondary">选择左侧产品查看详情</span>
            </div>
          ) : (
            <div className="paper-panel-elevated flex min-h-[520px] flex-col">

              {/* Header */}
              <div className="flex flex-col gap-2.5 border-b border-border px-4 py-4 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-[16px] font-semibold tracking-tight text-ink">{activeData.product}</span>
                  <span className="text-2xs font-mono tabular-nums text-ink-tertiary">{dateFrom} — {dateTo}</span>
                  <button
                    onClick={() => handleTriggerCrawl(activeData.product)}
                    disabled={crawling}
                    title={crawling ? "爬取中..." : `刷新「${activeData.product}」舆情数据`}
                    className="ml-1 p-1 rounded-md text-ink-faint transition-colors hover:bg-surface hover:text-ink-secondary disabled:opacity-40"
                  >
                    <svg className={`w-3.5 h-3.5 ${crawling && crawlProduct === activeData.product ? "animate-spin" : ""}`} fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 8A6 6 0 1 1 8 2" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v4h-4" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                  <StatBadge label="总帖数" value={activeData.post_count} cls="border border-border bg-surface text-ink" />
                  <StatBadge label="正面" value={activeData.positive} cls="border border-emerald-200/70 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300" />
                  <StatBadge label="负面" value={activeData.negative} cls="border border-rose-200/70 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300" />
                  <div className={`flex min-w-[84px] flex-col items-center rounded-xl px-3 py-2 ${riskColor(activeData.risk_index).bg}`}>
                    <span className={`data-num text-[15px] font-semibold leading-none ${riskColor(activeData.risk_index).text}`}>
                      {activeData.risk_index}
                    </span>
                    <span className={`mt-1 text-[10px] tracking-wide ${riskColor(activeData.risk_index).text} opacity-80`}>风险指数</span>
                  </div>
                  <button onClick={() => setActiveProduct(null)}
                    className="ml-1 text-ink-faint transition-colors hover:text-ink-secondary">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center overflow-x-auto border-b border-border px-4">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`-mb-px whitespace-nowrap border-b-2 px-3 py-3 text-xs font-medium transition-colors ${
                      activeTab === tab.id
                        ? "border-accent text-accent-dark dark:text-accent"
                        : "border-transparent text-ink-tertiary hover:text-ink-secondary"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 p-4 sm:p-5">
                {activeTab === "issues" && (
                  <div className="space-y-4">
                    <IssueDistChart data={issues} loading={issuesLoading} />
                    <div className="rounded-xl border border-border bg-surface px-4 py-4">
                      <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-ink-faint">AI 综合摘要</p>
                      <AISummary product={activeData.product} dateFrom={dateFrom} dateTo={dateTo} />
                    </div>
                  </div>
                )}

                {activeTab === "events" && (
                  <EventTimeline events={events} loading={eventsLoading} />
                )}

                {activeTab === "posts" && (
                  <PostsPanel product={activeData.product} dateFrom={dateFrom} dateTo={dateTo} />
                )}

                {activeTab === "cloud" && (
                  <WordCloudPanel product={activeData.product} dateFrom={dateFrom} dateTo={dateTo} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
